import { LOCALE_DIR, LOCALES, PREVIEW_LOCALES, isLocale, type Locale } from '@engineer-os/engine';

export { LOCALE_DIR, LOCALES, PREVIEW_LOCALES, isLocale, type Locale };

/**
 * Which language the page is in, and the one place that decides.
 *
 * Deliberately not React state. Three kinds of reader need this answer and
 * only one of them is a component: the components (which re-render, and get
 * it through the context in state/useLocale.ts), the plain modules that build
 * sentences without a hook — `verdict`, `lockedMessage`, the rank blurbs —
 * and the document itself, whose `lang` and `dir` have to be right before
 * React has rendered anything at all. A module-scope value with a subscriber
 * list serves all three; a context would serve only the first.
 *
 * The order below is the order of intent. A `?lang=` in the address bar is
 * somebody asking right now, and it wins and is remembered. A stored choice
 * is that same person having asked before. The browser's own languages are a
 * guess, and a good one, but only a guess. English is the source language and
 * the last resort.
 */

const KEY = 'engineeros.locale';

/**
 * Read once, at module load, and not part of the route.
 *
 * The router in App.tsx owns the query string and re-reads it on every
 * navigation; a language that lived there too would be lost the moment a
 * learner clicked through to a level, and would have to be threaded onto
 * every link in the product to survive. So `?lang=ar` is an entry point — it
 * makes a shareable Arabic link work — and the *choice* it expresses is kept
 * where choices are kept.
 */
function fromUrl(): Locale | null {
  try {
    const asked = new URLSearchParams(window.location.search).get('lang');
    return isLocale(asked) ? asked : null;
  } catch {
    return null;
  }
}

/**
 * There is a document to paint.
 *
 * False under `node --test`, which imports this module transitively whenever
 * it tests anything that reads a string. Everything below degrades to "the
 * source language, and nothing painted", which is the correct answer for a
 * process that has no page: a test asks the catalogue for a language by name
 * (`stringsFor('ar')`) rather than asking the page which one it is in.
 */
const inBrowser = typeof document !== 'undefined' && typeof window !== 'undefined';

function fromStorage(): Locale | null {
  if (!inBrowser) return null;
  try {
    const saved = window.localStorage.getItem(KEY);
    return isLocale(saved) ? saved : null;
  } catch {
    // Private mode, or storage switched off. Not knowing is survivable.
    return null;
  }
}

/**
 * The browser's languages, matched on the primary subtag.
 *
 * `ar-EG`, `ar-SA` and `ar` are all this course's Arabic — it is written in
 * Modern Standard Arabic and there is no regional variant to choose between.
 * Matching the subtag rather than the whole tag is what makes that true.
 */
function fromBrowser(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const asked = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of asked) {
    const primary = String(tag).toLowerCase().split('-')[0];
    // A preview language is never a guess (PREVIEW_LOCALES in
    // packages/engine/src/i18n.ts): its levels are not written yet, and a
    // reader who asked for nothing should not get half a course in it.
    if (isLocale(primary) && !PREVIEW_LOCALES.includes(primary)) return primary;
  }
  return null;
}

function detect(): Locale {
  if (!inBrowser) return 'en';
  return fromUrl() ?? fromStorage() ?? fromBrowser() ?? 'en';
}

let current: Locale = detect();

const listeners = new Set<(locale: Locale) => void>();

/**
 * Put the language on the document.
 *
 * `lang` is what tells a screen reader which voice to read in and the browser
 * which hyphenation and font fallbacks to use; `dir` is what turns the whole
 * layout around. Both belong on `<html>` rather than on a wrapper, because
 * `dir` on a wrapper leaves the scrollbar, the text selection and anything
 * portalled to `document.body` facing the other way.
 */
/**
 * The Arabic face, fetched the first time a page needs it.
 *
 * Archivo and Space Mono have no Arabic in them at all (styles/locale.css §1),
 * so without this an Arabic page falls through to whatever the operating
 * system keeps for the script and stops looking like one interface.
 *
 * It is not in index.html, and that is the point: an English reader must never
 * download a face they will not render. The link is added here, once, the
 * first time this module paints an Arabic document — on an Arabic first load
 * that is during module evaluation, ahead of the first render; on a switch it
 * is the click. `preconnect` for both Google hosts is already in the markup,
 * so the connection is warm either way.
 */
const ARABIC_FONT = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&display=swap';

let arabicFontRequested = false;

function loadArabicFont(): void {
  if (arabicFontRequested || !inBrowser) return;
  arabicFontRequested = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = ARABIC_FONT;
  document.head.append(link);
}

function paint(locale: Locale): void {
  if (!inBrowser) return;
  const root = document.documentElement;
  root.lang = locale;
  root.dir = LOCALE_DIR[locale];
  if (locale === 'ar') loadArabicFont();
}

// Before React. index.html ships `lang="en"` so that the markup is honest
// about the document it arrived as; this is the first thing that corrects it,
// and it runs during module evaluation, ahead of the first render.
paint(current);

export function getLocale(): Locale {
  return current;
}

export function getDir(): 'ltr' | 'rtl' {
  return LOCALE_DIR[current];
}

/** True when the page is set right to left, for the handful of places that must ask. */
export function isRtl(): boolean {
  return LOCALE_DIR[current] === 'rtl';
}

/**
 * Change the language.
 *
 * Idempotent, and quiet when nothing changed: this is called from a toggle
 * that can be clicked twice, and from the effect that syncs a restored
 * preference, and neither should cost a re-render of the whole tree for
 * nothing.
 */
export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  paint(locale);
  try {
    if (inBrowser) window.localStorage.setItem(KEY, locale);
  } catch {
    // The choice still holds for this visit; it just will not outlive it.
  }
  for (const listener of listeners) listener(locale);
}

/** Subscribe to language changes. Returns the unsubscribe. */
export function onLocale(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const localisedListeners = new Set<(locale: Locale) => void>();

/**
 * The levels have been rewritten in `locale`. Called by `applyLocale`
 * (engine/runtime.ts) once it has finished writing, and by nothing else.
 *
 * A second signal because `onLocale` fires too early for anything holding a
 * level's words. The chrome is translated the moment the language changes —
 * its catalogue is compiled in — but a level's brief, objectives and hints
 * come from a file per language, and they are written onto the mission
 * objects only after that file has arrived. A reader that re-read on
 * `onLocale` would re-read the old language.
 *
 * The reader it exists for is the open level. `useSession` took its own copy
 * of the mission, the revealed hints and the reflection when the level opened,
 * and that copy did not follow a switch: the chrome turned Arabic around a
 * brief that stayed English until a reload — and a reload reboots the machine
 * and reseeds the workspace, so the learner paid for the language with their
 * work.
 */
export function levelsLocalised(locale: Locale): void {
  for (const listener of [...localisedListeners]) listener(locale);
}

/** Subscribe to the levels being rewritten in a language. Returns the unsubscribe. */
export function onLevelsLocalised(listener: (locale: Locale) => void): () => void {
  localisedListeners.add(listener);
  return () => localisedListeners.delete(listener);
}

/**
 * The languages the switch offers right now.
 *
 * Every launched language, plus a preview one only while the page is in it:
 * somebody who arrived by `?lang=de` has to be able to see where they are and
 * get back out, and nobody else should find a language whose levels are still
 * English. While that leaves two, the control stays a switch — the same
 * button, the same width, the same corner; a third makes it a menu
 * (components/LangToggle.tsx).
 */
export function offeredLocales(locale: Locale = current): Locale[] {
  return LOCALES.filter((l) => !PREVIEW_LOCALES.includes(l) || l === locale);
}

/**
 * The other language, for the switch while it has exactly two to offer.
 *
 * With three on offer this returns the next one round, which is a worse
 * control but not a broken one; the switch turns into a menu then and does
 * not call it.
 */
export function otherLocale(locale: Locale = current): Locale {
  const offered = offeredLocales(locale);
  const i = offered.indexOf(locale);
  return offered[(i + 1) % offered.length]!;
}

/**
 * What each language calls itself.
 *
 * An endonym, never a translation: somebody looking for Arabic is looking for
 * the word "العربية", and a control that offered them "Arabic" would be
 * written in the language they cannot read. Same reason the reverse is true.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  de: 'Deutsch',
  ja: '日本語',
};

/** The two-or-three letters a compact toggle shows. */
export const LOCALE_SHORT: Record<Locale, string> = {
  en: 'EN',
  ar: 'ع',
  de: 'DE',
  ja: 'JA',
};
